import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

/**
 * Stepper
 *
 * Animated, responsive step progress bar (RTL + LTR aware).
 *
 * Usage:
 * <Stepper
 *   steps={["Basic Details", "Units Details", "Documents"]}
 *   currentStep={0}
 *   dir="ltr" // or "rtl"
 * />
 *
 * - currentStep is 0-indexed: 0 = first step in progress/active
 * - A step is "done" if its index < currentStep
 * - A step is "active" if its index === currentStep
 * - Connector lines animate (width grows) when you move to the next step
 * - Works with dir="rtl" — connector fill direction, chevrons, and layout all flip automatically
 */
export default function Stepper({
  steps = [],
  currentStep = 0,
  dir = 'ltr',
  className = '',
  onProgressBarClick = (index: number) => {},
}) {
  const isRtl = dir === 'rtl';
  // Track which connectors have already animated so re-renders don't replay the animation
  const [filledConnectors, setFilledConnectors] = useState(
    () => new Set(Array.from({ length: currentStep }, (_, i) => i))
  );
  const prevStep = useRef(currentStep);

  useEffect(() => {
    if (currentStep > prevStep.current) {
      // Moving forward: animate connectors between prevStep and currentStep one by one
      const toFill = [];
      for (let i = prevStep.current; i < currentStep; i++) toFill.push(i);
      toFill.forEach((idx, order) => {
        setTimeout(() => {
          setFilledConnectors((prev) => new Set(prev).add(idx));
        }, order * 150);
      });
    } else if (currentStep < prevStep.current) {
      // Moving backward: un-fill connectors instantly (no need to animate reverse)
      setFilledConnectors(
        (prev) => new Set(Array.from(prev).filter((idx) => idx < currentStep))
      );
    }
    prevStep.current = currentStep;
  }, [currentStep]);

  return (
    <div
      dir={dir}
      className={`w-full ${className}`}
      role="list"
      aria-label="Progress"
    >
      <div className="flex items-start w-full">
        {steps.map((label, index) => {
          const isDone = index < currentStep;
          const isActive = index === currentStep;
          const isLast = index === steps.length - 1;
          const connectorFilled = filledConnectors.has(index);

          return (
            <React.Fragment key={label}>
              {/* Step node + label */}
              <div
                role="listitem"
                aria-current={isActive ? 'step' : undefined}
                className="flex flex-col items-center shrink-0 btnEffect"
                style={{ minWidth: 0 }}
                onClick={() => onProgressBarClick(index)}
              >
                <div
                  className={`relative flex items-center justify-center rounded-full transition-all duration-300 ease-out
                    w-8 h-8 sm:w-10 sm:h-10
                    ${
                      isDone
                        ? 'bg-emerald-600 scale-100'
                        : isActive
                        ? 'bg-white border-2 border-emerald-600 scale-105'
                        : 'bg-white border-2 border-gray-300 scale-100'
                    }`}
                >
                  {isDone ? (
                    <Check
                      className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-[checkPop_0.3s_ease-out]"
                      strokeWidth={3}
                    />
                  ) : (
                    <span
                      className={`text-xs sm:text-sm font-semibold ${
                        isActive ? 'text-emerald-600' : 'text-gray-400'
                      }`}
                    >
                      {index + 1}
                    </span>
                  )}
                  {isActive && (
                    <span className="absolute inset-0 rounded-full ring-4 ring-emerald-100 animate-pulse" />
                  )}
                </div>

                <span
                  className={`mt-2 text-xs sm:text-sm font-semibold text-center px-1 leading-tight
                    ${isDone || isActive ? 'text-emerald-700' : 'text-gray-400'}`}
                >
                  {label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex-1 flex items-center px-1 sm:px-2 mt-4 sm:mt-5">
                  <div className="relative w-full h-[3px] rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="absolute top-0 bottom-0 bg-emerald-600 rounded-full transition-all duration-500 ease-out"
                      style={{
                        width: connectorFilled ? '100%' : '0%',
                        // In RTL, fill should grow from the right edge instead of the left
                        [isRtl ? 'right' : 'left']: 0,
                      }}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <style>{`
        @keyframes checkPop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
